/**
 * i18n — 로컬라이제이션 + 테마 오버라이드
 * 
 * 우선순위: theme > locale
 * 사용법:
 *   import { t, tc, tm, te, tq, setLocale, setTheme } from './i18n';
 *   t('ui.login')         → '로그인'
 *   tc('strike')          → { name: '강타', description: '...' }
 *   tm('goblin')          → '고블린'
 *   te('goblin_hunter_weapon') → { name: '...', description: '...' }
 *   tq('battle_master')   → { name: '...', description: '...' }
 */

import ko from '../locales/ko.json' with { type: 'json' };
import en from '../locales/en.json' with { type: 'json' };

const locales = { ko, en };
const savedLang = (() => { try { return localStorage.getItem('language'); } catch { return null; } })() || 'ko';

let currentLocale = locales[savedLang] || ko;
let currentTheme = null;

// 언어 변경
export function setLanguage(lang) {
  if (locales[lang]) {
    currentLocale = locales[lang];
    if (typeof localStorage !== 'undefined') try { localStorage.setItem('language', lang); } catch { }
  }
}

// Locale 변경 (직접 객체 주입)
export function setLocale(locale) {
  currentLocale = locale;
}

// Theme 변경 (locale 위에 오버라이드)
export function setTheme(theme) {
  currentTheme = theme;
}

// Get current locale data (for external access)
export function getLocale() {
  return currentLocale;
}

// 일반 UI 문자열 (params 지원 추가)
export function t(key, paramsOrFallback) {
  const keys = key.split('.');
  let val = currentLocale;
  for (const k of keys) {
    val = val?.[k];
    if (val === undefined) {
      return (typeof paramsOrFallback === 'string' ? paramsOrFallback : key);
    }
  }

  if (typeof val === 'string' && typeof paramsOrFallback === 'object') {
    return val.replace(/{(\w+)}/g, (_, k) => paramsOrFallback[k] !== undefined ? paramsOrFallback[k] : `{${k}}`);
  }

  return val;
}

// 클래스 이름
export function tClass(classId) {
  return currentTheme?.classes?.[classId]
    || currentLocale.classes?.[classId]
    || classId;
}

// 카드 (name + description)
export function tc(cardId) {
  const themed = currentTheme?.cards?.[cardId];
  const base = currentLocale.cards?.[cardId];
  return {
    name: themed?.name || base?.name || cardId,
    description: themed?.description || base?.description || '',
    artUrl: themed?.artUrl || null,
  };
}

// 몬스터 이름
export function tm(monsterId) {
  return currentTheme?.monsters?.[monsterId]
    || currentLocale.monsters?.[monsterId]
    || monsterId;
}

// 용병 이름
export function tMerc(mercId) {
  return currentTheme?.mercenaries?.[mercId]
    || currentLocale.mercenaries?.[mercId]
    || mercId;
}

// 장비 (name + description)
export function te(equipId) {
  const themed = currentTheme?.equipment?.[equipId];
  const base = currentLocale.equipment?.[equipId];
  return {
    name: themed?.name || base?.name || equipId,
    description: themed?.description || base?.description || '',
  };
}

// 퀘스트 (name + description)
export function tq(questId) {
  const themed = currentTheme?.quests?.[questId];
  const base = currentLocale.quests?.[questId];
  return {
    name: themed?.name || base?.name || questId,
    description: themed?.description || base?.description || '',
  };
}

// 존 이름
export function tZone(zoneId) {
  return currentTheme?.zones?.[zoneId]
    || currentLocale.zones?.[zoneId]
    || zoneId;
}

// 이벤트 이름
export function tEvent(eventType) {
  return currentLocale.events?.[eventType] || eventType;
}
